import { useEffect, useState } from "react";
import type { FollowedItem, FollowsResponse } from "@skills-marketplace/contracts/contract-11-follows";
import EmptyState from "../components/EmptyState";
import { useFollows } from "../lib/follows-context";
import { postVisit } from "../lib/follow-client";
import { detailHrefForInstallKey } from "../lib/catalog-context";
import { reportEvent } from "../lib/newrelic";
import ArrowUpIcon from "../icons/ArrowUpIcon";
import AlertTriangleIcon from "../icons/AlertTriangleIcon";
import StarIcon from "../icons/StarIcon";
import styles from "./FollowingPage.module.css";

const CHANGE_BADGE = {
  new: { label: "New since your last visit", Icon: StarIcon },
  updated: { label: "Updated since your last visit", Icon: ArrowUpIcon },
  inactive: { label: "Now inactive", Icon: AlertTriangleIcon },
} as const;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function provenance(item: FollowedItem, displayNameOf: (installName: string) => string): string | null {
  if (item.kind === "plugin") {
    const count = item.includedSkills?.length ?? 0;
    return count > 0 ? `Following the whole plug-in. This includes its ${count} skills.` : null;
  }
  if (!item.viaPlugin) return null;
  const plugin = displayNameOf(item.viaPlugin);
  if (item.followedDirectly) return `Following this skill only, from the ${plugin} plug-in`;
  return item.change === "new"
    ? `Added automatically because you follow the ${plugin} plug-in`
    : `Following via the ${plugin} plug-in`;
}

function nestsUnder(child: FollowedItem, plugin: FollowedItem): boolean {
  return child.viaPlugin !== undefined && child.viaPlugin === plugin.installName;
}

function topLevelItems(items: FollowedItem[]): FollowedItem[] {
  return items.filter(
    (item) => !items.some((plugin) => nestsUnder(item, plugin)),
  );
}

function FollowedItemCard({
  item,
  childItems,
  response,
  onUnfollow,
}: {
  item: FollowedItem;
  childItems: FollowedItem[];
  response: FollowsResponse;
  onUnfollow: (item: FollowedItem) => void;
}) {
  const displayNameOf = (installName: string) =>
    response.items.find((candidate) => candidate.installName === installName)?.displayName ?? installName;
  const inactive = item.status === "inactive";
  const note = provenance(item, displayNameOf);
  const badge = item.change ? CHANGE_BADGE[item.change] : null;

  return (
    <li
      className={`${styles.card} ${inactive ? styles.inactive : ""} ${item.change ? styles.changed : ""}`}
    >
      <div className={styles.header}>
        <span className={`${styles.kind} ${item.kind === "plugin" ? styles.kindPlugin : styles.kindSkill}`}>
          {item.kind === "plugin" ? "Plugin" : "Skill"}
        </span>
        {badge && (
          <span className={styles.badge}>
            <badge.Icon className={styles.badgeIcon} />
            {badge.label}
          </span>
        )}
        <span className={styles.version}>v{item.version}</span>
      </div>

      <h2 className={styles.name}>
        {inactive ? (
          item.displayName
        ) : (
          <a href={detailHrefForInstallKey(item.installName)}>{item.displayName}</a>
        )}
      </h2>
      <p className={styles.repo}>
        {item.orgOrOwner}/{item.repoName}
      </p>

      {inactive ? (
        <div className={styles.unavailable}>
          <p>This item is no longer available.</p>
          {item.inactiveSince && <p>Inactive since {formatDate(item.inactiveSince)}</p>}
        </div>
      ) : (
        <p className={styles.updated}>Last updated {formatDate(item.lastUpdatedAt)}</p>
      )}

      {note && <p className={styles.note}>{note}</p>}

      {childItems.length > 0 && (
        <details className={styles.included}>
          <summary>
            {childItems.length} {childItems.length === 1 ? "skill" : "skills"} included
          </summary>
          <ul className={styles.children}>
            {childItems.map((child) => (
              <FollowedItemCard
                key={child.installName}
                item={child}
                childItems={[]}
                response={response}
                onUnfollow={onUnfollow}
              />
            ))}
          </ul>
        </details>
      )}

      <div className={styles.actions}>
        {item.followedDirectly && (
          <button type="button" className={styles.unfollow} onClick={() => onUnfollow(item)}>
            Unfollow {item.displayName}
          </button>
        )}
      </div>
    </li>
  );
}

/** Membership from the live list; change flags and their count as shown on entry. */
function withEntryFlags(live: FollowsResponse, entry: FollowsResponse): FollowsResponse {
  const flags = new Map(entry.items.map((item) => [item.installName, item.change]));
  const items = live.items.map((item) => ({ ...item, change: flags.get(item.installName) ?? null }));
  return { ...live, items, changedCount: items.filter((item) => item.change).length };
}

/**
 * The caller's followed items with change flags since their last visit. Entering the page
 * refreshes the list and records one visit for that fetch, so the flags survive later writes
 * on this page and clear on the next visit.
 */
export default function FollowingPage() {
  const { enabled, status, response: live, refresh, toggle } = useFollows();
  const [entry, setEntry] = useState<FollowsResponse | null>(null);
  const response = live && entry ? withEntryFlags(live, entry) : live;

  useEffect(() => {
    let entered = true;
    void refresh().then((next) => {
      if (!entered || !next) return;
      setEntry(next);
      void postVisit({ asOf: next.asOf, familySnapshot: next.familySnapshot });
      reportEvent("marketplace_following_visit", { totalCount: next.totalCount, changedCount: next.changedCount });
    });
    return () => {
      entered = false;
    };
    // Only on entering the page; the provider keeps the list current after writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!enabled) return null;

  return (
    <section className={styles.page}>
      <h1>Following</h1>
      <p className={styles.blurb}>
        Changes to what you follow show here the next time you open this page. Only you can see this list.
      </p>

      {status === "loading" && !response && <p role="status">Loading your follows…</p>}

      {status === "error" && !response && (
        <div role="alert" className={styles.retry}>
          <p>We couldn't load your follows.</p>
          <button type="button" onClick={() => void refresh()}>
            Try again
          </button>
        </div>
      )}

      {response && response.totalCount === 0 && (
        <EmptyState message="You aren't following anything yet.">
          <p>
            <a href="#/">Browse the catalog</a> and choose Follow on a plug-in or skill to keep track of it here.
          </p>
        </EmptyState>
      )}

      {response && response.totalCount > 0 && (
        <>
          <p className={styles.summary}>
            {response.totalCount} followed
            {response.changedCount > 0 && (
              <span className={styles.changedPill}>{response.changedCount} changed since your last visit</span>
            )}
          </p>
          <ul className={styles.list}>
            {topLevelItems(response.items).map((item) => (
              <FollowedItemCard
                key={item.installName}
                item={item}
                childItems={response.items.filter((child) => nestsUnder(child, item))}
                response={response}
                onUnfollow={(target) => void toggle(target.installName, target.displayName, false)}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
